import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { settingsApi } from '@/lib/api'

const SETTINGS_STORAGE_KEY = 'locky-settings'
const USER_SETTINGS_STORAGE_PREFIX = 'locky-settings:'

const AUTO_LOCK_OPTIONS = [
  { ms: 1 * 60 * 1000, label: '1 min' },
  { ms: 5 * 60 * 1000, label: '5 min' },
  { ms: 15 * 60 * 1000, label: '15 min' },
  { ms: 30 * 60 * 1000, label: '30 min' },
  { ms: 60 * 60 * 1000, label: '1 h' },
]

const CLIPBOARD_OPTIONS = [10_000, 30_000, 60_000, 120_000]

const DEFAULT_PASSWORD_GENERATOR_PROFILE = {
  complex: {
    length: 20,
    useLower: true,
    useUpper: true,
    useDigits: true,
    useSymbols: true,
    excludeAmbiguous: true,
  },
  words: {
    words: 5,
    addNumber: true,
  },
  initials: {
    phraseWords: 12,
  },
}

const DEFAULT_PASSWORD_GENERATOR_SETTINGS = {
  vault: mergePasswordGeneratorProfile(),
  entry: mergePasswordGeneratorProfile(),
}

const DEFAULT_SETTINGS = {
  theme: 'system',
  language: 'fr',
  autoLockMs: 15 * 60 * 1000,
  lockOnBlur: false,
  clipboardClearMs: 30_000,
  passwordGenerator: DEFAULT_PASSWORD_GENERATOR_SETTINGS,
}

let pendingSettingsPatch = null
let syncTimer = null
let syncInFlight = false

function mergePasswordGeneratorProfile(profile = {}) {
  const current = profile ?? {}
  return {
    complex: {
      ...DEFAULT_PASSWORD_GENERATOR_PROFILE.complex,
      ...current.complex,
    },
    words: {
      ...DEFAULT_PASSWORD_GENERATOR_PROFILE.words,
      ...current.words,
    },
    initials: {
      ...DEFAULT_PASSWORD_GENERATOR_PROFILE.initials,
      ...current.initials,
    },
  }
}

function mergePasswordGeneratorSettings(settings = {}) {
  const current = settings ?? {}
  if (current.vault || current.entry) {
    return {
      vault: mergePasswordGeneratorProfile(current.vault),
      entry: mergePasswordGeneratorProfile(current.entry),
    }
  }

  return {
    vault: mergePasswordGeneratorProfile(current),
    entry: mergePasswordGeneratorProfile(current),
  }
}

function mergeSettings(settings = {}) {
  const current = settings ?? {}
  const autoLockMs = Number(current.autoLockMs)
  const clipboardClearMs = Number(current.clipboardClearMs)

  return {
    theme: ['light', 'dark', 'system'].includes(current.theme) ? current.theme : DEFAULT_SETTINGS.theme,
    language: ['fr', 'en'].includes(current.language) ? current.language : DEFAULT_SETTINGS.language,
    autoLockMs: AUTO_LOCK_OPTIONS.some((option) => option.ms == autoLockMs) ? autoLockMs : DEFAULT_SETTINGS.autoLockMs,
    lockOnBlur: typeof current.lockOnBlur == 'boolean' ? current.lockOnBlur : DEFAULT_SETTINGS.lockOnBlur,
    clipboardClearMs: CLIPBOARD_OPTIONS.includes(clipboardClearMs) ? clipboardClearMs : DEFAULT_SETTINGS.clipboardClearMs,
    passwordGenerator: mergePasswordGeneratorSettings(current.passwordGenerator),
  }
}

function settingsFromState(state) {
  return mergeSettings({
    theme: state.theme,
    language: state.language,
    autoLockMs: state.autoLockMs,
    lockOnBlur: state.lockOnBlur,
    clipboardClearMs: state.clipboardClearMs,
    passwordGenerator: state.passwordGenerator,
  })
}

function updatePasswordGeneratorSection(state, profileKey, sectionKey, values) {
  const current = mergePasswordGeneratorSettings(state.passwordGenerator)
  const profile = current[profileKey] ?? mergePasswordGeneratorProfile()
  return {
    passwordGenerator: {
      ...current,
      [profileKey]: {
        ...profile,
        [sectionKey]: { ...profile[sectionKey], ...values },
      },
    },
  }
}

function deepMerge(base = {}, patch = {}) {
  const result = { ...base }
  for (const [key, value] of Object.entries(patch ?? {})) {
    if (value === undefined) continue
    result[key] = isPlainObject(value) && isPlainObject(base?.[key])
      ? deepMerge(base[key], value)
      : value
  }
  return result
}

function isPlainObject(value) {
  return Boolean(value) && typeof value == 'object' && !Array.isArray(value)
}

function enqueueSettingsPatch(get, set, patch) {
  if (!get().syncUserSub) return

  pendingSettingsPatch = deepMerge(pendingSettingsPatch ?? {}, patch)
  set({ syncStatus: 'saving', syncError: null, pendingLocalChanges: true })

  if (syncTimer) clearTimeout(syncTimer)
  syncTimer = setTimeout(() => { flushSettingsPatch(get, set) }, 300)
}

async function flushSettingsPatch(get, set) {
  if (syncInFlight || !pendingSettingsPatch || !get().syncUserSub) return

  syncInFlight = true
  while (pendingSettingsPatch && get().syncUserSub) {
    const patch = pendingSettingsPatch
    pendingSettingsPatch = null

    try {
      set({ syncStatus: 'saving', syncError: null })
      const response = await settingsApi.patch(patch)
      if (!pendingSettingsPatch) {
        set({
          ...mergeSettings(response?.settings),
          serverLoaded: true,
          serverSource: response?.source ?? 'database',
          syncStatus: 'idle',
          syncError: null,
          pendingLocalChanges: false,
          lastSyncedAt: response?.updatedAt ?? new Date().toISOString(),
        })
      }
    }
    catch (err) {
      pendingSettingsPatch = deepMerge(patch, pendingSettingsPatch ?? {})
      set({
        syncStatus: 'error',
        syncError: err?.message ?? 'Settings sync failed',
        pendingLocalChanges: true,
      })
      break
    }
  }
  syncInFlight = false
}

function updateSettings(get, set, patch) {
  set((state) => mergeSettings(deepMerge(settingsFromState(state), patch)))
  enqueueSettingsPatch(get, set, patch)
}

function userSettingsStorageName(userSub) {
  return `${USER_SETTINGS_STORAGE_PREFIX}${userSub}`
}

function hasStoredSettings(name) {
  if (typeof localStorage == 'undefined') return false
  return Boolean(localStorage.getItem(name))
}

function readStoredSettings(name) {
  if (typeof localStorage == 'undefined') return null

  try {
    const raw = localStorage.getItem(name)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return mergeSettings(parsed?.state ?? parsed)
  }
  catch {
    return null
  }
}

export const useSettingsStore = create(
  persist(
    (set, get) => ({
      ...mergeSettings(),
      serverLoaded: false,
      serverSource: 'local',
      syncStatus: 'idle',
      syncError: null,
      syncUserSub: null,
      pendingLocalChanges: false,
      lastSyncedAt: null,

      hydrateFromServer: (settings, metadata = {}) => set({
        ...mergeSettings(settings),
        serverLoaded: true,
        serverSource: metadata.source ?? 'database',
        syncStatus: 'idle',
        syncError: null,
        pendingLocalChanges: false,
        lastSyncedAt: metadata.updatedAt ?? new Date().toISOString(),
      }),
      disableServerSync: () => {
        if (syncTimer) clearTimeout(syncTimer)
        pendingSettingsPatch = null
        syncInFlight = false
        set({
          serverLoaded: false,
          serverSource: 'local',
          syncStatus: 'idle',
          syncError: null,
          syncUserSub: null,
          pendingLocalChanges: false,
          lastSyncedAt: null,
        })
      },

      setTheme: (theme) => updateSettings(get, set, { theme }),
      setLanguage: (language) => updateSettings(get, set, { language }),
      setAutoLockMs: (autoLockMs) => updateSettings(get, set, { autoLockMs }),
      setLockOnBlur: (lockOnBlur) => updateSettings(get, set, { lockOnBlur }),
      setClipboardClearMs: (clipboardClearMs) => updateSettings(get, set, { clipboardClearMs }),
      setPasswordGeneratorComplex: (profileKey, complex) => {
        const next = updatePasswordGeneratorSection(get(), profileKey, 'complex', complex)
        set(next)
        enqueueSettingsPatch(get, set, next)
      },
      setPasswordGeneratorWords: (profileKey, words) => {
        const next = updatePasswordGeneratorSection(get(), profileKey, 'words', words)
        set(next)
        enqueueSettingsPatch(get, set, next)
      },
      setPasswordGeneratorInitials: (profileKey, initials) => {
        const next = updatePasswordGeneratorSection(get(), profileKey, 'initials', initials)
        set(next)
        enqueueSettingsPatch(get, set, next)
      },
    }),
    {
      name: SETTINGS_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: settingsFromState,
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...mergeSettings(persistedState),
      }),
    },
  ),
)

export async function initializeUserSettingsSync(userSub) {
  const normalizedUserSub = String(userSub ?? '').trim()
  if (!normalizedUserSub) {
    useSettingsStore.getState().disableServerSync()
    return null
  }

  const storageName = userSettingsStorageName(normalizedUserSub)
  const hasUserCache = hasStoredSettings(storageName)
  useSettingsStore.persist.setOptions({ name: storageName })
  useSettingsStore.setState({
    syncUserSub: normalizedUserSub,
    serverLoaded: false,
    serverSource: hasUserCache ? 'cache' : 'local',
    syncStatus: 'loading',
    syncError: null,
    pendingLocalChanges: false,
  })

  if (hasUserCache) {
    await useSettingsStore.persist.rehydrate()
    useSettingsStore.setState({
      syncUserSub: normalizedUserSub,
      serverLoaded: false,
      serverSource: 'cache',
      syncStatus: 'loading',
      syncError: null,
      pendingLocalChanges: false,
    })
  } else {
    useSettingsStore.setState({ ...mergeSettings() })
  }

  try {
    const response = await settingsApi.get()
    let settings = mergeSettings(response?.settings)
    let source = response?.source ?? 'database'
    let updatedAt = response?.updatedAt ?? null

    if (response?.source == 'defaults' && !hasUserCache) {
      const legacySettings = readStoredSettings(SETTINGS_STORAGE_KEY)
      if (legacySettings) {
        const saved = await settingsApi.put(legacySettings)
        settings = mergeSettings(saved?.settings)
        source = saved?.source ?? 'database'
        updatedAt = saved?.updatedAt ?? null
        try { localStorage.removeItem(SETTINGS_STORAGE_KEY) } catch { /* ignore legacy cleanup failure */ }
      }
    }

    if (!useSettingsStore.getState().pendingLocalChanges) {
      useSettingsStore.getState().hydrateFromServer(settings, { source, updatedAt })
    } else {
      useSettingsStore.setState({ serverLoaded: true, serverSource: source })
    }

    return settings
  }
  catch (err) {
    useSettingsStore.setState({
      serverLoaded: false,
      syncStatus: 'error',
      syncError: err?.message ?? 'Settings load failed',
    })
    return null
  }
}

export function disableUserSettingsSync() {
  useSettingsStore.getState().disableServerSync()
  useSettingsStore.persist.setOptions({ name: SETTINGS_STORAGE_KEY })
}

export {
  AUTO_LOCK_OPTIONS,
  CLIPBOARD_OPTIONS,
  DEFAULT_PASSWORD_GENERATOR_SETTINGS,
  DEFAULT_SETTINGS,
  mergeSettings,
}
